import { CdkObserveContent } from '@angular/cdk/observers';
import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, computed, ContentChild, ElementRef, inject, input, PLATFORM_ID, ViewChild } from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { lucideChevronDown } from '@ng-icons/lucide';
import { SonaIconComponent } from '@sona/angular-ui-components';
import { sonaHlm } from '@sona/angular-util-common';
import { hlm } from '@spartan-ng/ui-core';
import { HlmIconComponent } from '@spartan-ng/ui-icon-helm';
import { BrnSelectComponent, BrnSelectTriggerDirective, BrnSelectService } from '@spartan-ng/ui-select-brain';
import { cva, type VariantProps } from 'class-variance-authority';

import type { ClassValue } from 'clsx';

export const selectTriggerVariants = cva(
    'tw-flex tw-items-center tw-justify-between tw-rounded-md tw-text-sm tw-ring-offset-background placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50',
    {
        variants: {
            variant: {
                default: 'tw-bg-background tw-border tw-border-input',
                ghost: 'tw-bg-transparent hover:tw-bg-accent hover:tw-text-accent-foreground'
            },
            size: {
                default: 'tw-h-10 tw-py-2 tw-px-4',
                sm: 'tw-h-9 tw-px-3',
                lg: 'tw-h-11 tw-px-8'
            },
            error: {
                auto: '[&.ng-invalid.ng-touched]:tw-text-destructive [&.ng-invalid.ng-touched]:tw-border-destructive [&.ng-invalid.ng-touched]:focus-visible:tw-ring-destructive',
                true: 'tw-text-destructive tw-border-destructive focus-visible:tw-ring-destructive'
            }
        },
        defaultVariants: {
            size: 'default',
            error: 'auto'
        }
    }
);
type SelectTriggerVariants = VariantProps<typeof selectTriggerVariants>;

@Component({
    selector: 'hlm-select-trigger',
    imports: [ BrnSelectTriggerDirective, HlmIconComponent, CdkObserveContent ],
    providers: [provideIcons({ lucideChevronDown })],
    template: `
		<button [class]="_computedClass()" #button hlmInput brnSelectTrigger type="button" (cdkObserveContent)="updateTriggerWidth()">
			<ng-content />
			@if (icon || sonaIcon) {
				<ng-content select="hlm-icon,sona-icon" />
			} @else {
				<hlm-icon class="tw-ml-2 tw-h-4 tw-w-4 tw-flex-none" name="lucideChevronDown" />
			}
		</button>
	`
})
export class HlmSelectTriggerComponent implements AfterViewInit {

    @ViewChild('button', { static: true })
    public buttonEl!: ElementRef;

    @ContentChild(HlmIconComponent, { static: false })
    protected icon!: HlmIconComponent;

    @ContentChild(SonaIconComponent, { static: false })
    protected sonaIcon!: unknown;

    protected readonly brnSelect = inject(BrnSelectComponent, { optional: true });

    public readonly variant = input<SelectTriggerVariants['variant']>('default');
    public readonly _size = input<SelectTriggerVariants['size']>('default');
    public readonly userClass = input<ClassValue>('', { alias: 'class' });

    protected _computedClass = computed(() =>
        sonaHlm(selectTriggerVariants({
            variant: this.variant(),
            size: this._size(),
            error: this.brnSelect?.errorState()
        }), this.userClass())
    );

    private readonly _platform = inject(PLATFORM_ID);
    private readonly _elementRef = inject(ElementRef);
    private readonly _selectService = inject(BrnSelectService);
    private _resizeObserver?: ResizeObserver;

    ngAfterViewInit() {
        // if we are on the client, listen for element resize events
        if (isPlatformBrowser(this._platform)) {
            this._resizeObserver = new ResizeObserver(() => this.updateTriggerWidth());

            this._resizeObserver.observe(this._elementRef.nativeElement);
        }
    }

    protected updateTriggerWidth() {
        this._selectService.setTriggerWidth(this._elementRef.nativeElement.offsetWidth);
    }
}
